import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const VOID_BG = '#080810';
const CARD_BG = '#0D0D1A';
const CYAN = '#00FFE5';
const ICE = '#E8F4FF';
const MUTED = '#8899AA';
const ERROR = '#FF4444';
const MONO = "'JetBrains Mono', 'Courier New', monospace";
const ORBITRON = "'Orbitron', monospace";

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    agreeToTerms: false, subscribeNewsletter: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const validatePassword = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = (strength) => ['VERY WEAK', 'WEAK', 'FAIR', 'STRONG', 'MAXIMUM']?.[strength] || 'VERY WEAK';
  const getPasswordStrengthColor = (strength) => [ERROR, ERROR, '#FFD700', CYAN, CYAN]?.[strength] || ERROR;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (name === 'password') setPasswordStrength(validatePassword(value));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.firstName?.trim()) newErrors.firstName = 'FIRST NAME REQUIRED';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'LAST NAME REQUIRED';
    if (!formData?.email?.trim()) newErrors.email = 'EMAIL REQUIRED';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) newErrors.email = 'INVALID EMAIL FORMAT';
    if (!formData?.password) newErrors.password = 'PASSWORD REQUIRED';
    else if (formData?.password?.length < 8) newErrors.password = 'MINIMUM 8 CHARACTERS';
    if (!formData?.confirmPassword) newErrors.confirmPassword = 'CONFIRM YOUR PASSWORD';
    else if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'PASSWORDS DO NOT MATCH';
    if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'MUST ACCEPT TERMS';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) onSubmit(formData);
  };

  const inputStyle = {
    width: '100%', background: '#0A0A14', border: '1px solid rgba(0,255,229,0.3)',
    color: ICE, padding: '10px 14px', fontFamily: MONO, fontSize: '0.82rem', outline: 'none', transition: 'border-color 0.2s',
  };
  const labelStyle = {
    display: 'block', color: CYAN, fontFamily: MONO, fontSize: '0.65rem',
    letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700,
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h1 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: '1.8rem', color: ICE, marginBottom: '0.5rem' }}>SIGN UP NOW</h1>
        <p style={{ color: MUTED, fontFamily: MONO, fontSize: '0.75rem' }}>Initialize your commander account</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { name: 'firstName', label: 'First Name', placeholder: 'First' },
            { name: 'lastName', label: 'Last Name', placeholder: 'Last' },
          ]?.map(field => (
            <div key={field?.name}>
              <label style={labelStyle}>{field?.label}</label>
              <input type="text" name={field?.name} value={formData?.[field?.name]} onChange={handleInputChange}
                placeholder={field?.placeholder} disabled={isLoading}
                style={{ ...inputStyle, borderColor: errors?.[field?.name] ? ERROR : 'rgba(0,255,229,0.3)' }}
                onFocus={e => e.target.style.borderColor = CYAN}
                onBlur={e => e.target.style.borderColor = errors?.[field?.name] ? ERROR : 'rgba(0,255,229,0.3)'}
              />
              {errors?.[field?.name] && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.[field?.name]}</p>}
            </div>
          ))}
        </div>

        <div>
          <label style={labelStyle}>Email Address</label>
          <input type="email" name="email" value={formData?.email} onChange={handleInputChange}
            placeholder="commander@domain.com" disabled={isLoading}
            style={{ ...inputStyle, borderColor: errors?.email ? ERROR : 'rgba(0,255,229,0.3)' }}
            onFocus={e => e.target.style.borderColor = CYAN}
            onBlur={e => e.target.style.borderColor = errors?.email ? ERROR : 'rgba(0,255,229,0.3)'}
          />
          {errors?.email && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.email}</p>}
        </div>

        <div className="relative">
          <label style={labelStyle}>Password</label>
          <input type={showPassword ? 'text' : 'password'} name="password" value={formData?.password} onChange={handleInputChange}
            placeholder="Create strong password" disabled={isLoading}
            style={{ ...inputStyle, borderColor: errors?.password ? ERROR : 'rgba(0,255,229,0.3)', paddingRight: '44px' }}
            onFocus={e => e.target.style.borderColor = CYAN}
            onBlur={e => e.target.style.borderColor = errors?.password ? ERROR : 'rgba(0,255,229,0.3)'}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isLoading}
            style={{ position: 'absolute', right: '12px', top: '34px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
          {formData?.password && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-1" style={{ background: 'rgba(0,255,229,0.1)' }}>
                  <div style={{ height: '100%', width: `${(passwordStrength / 5) * 100}%`, background: getPasswordStrengthColor(passwordStrength), transition: 'all 0.3s' }} />
                </div>
                <span style={{ fontSize: '0.6rem', color: getPasswordStrengthColor(passwordStrength), fontFamily: MONO, letterSpacing: '0.08em' }}>{getPasswordStrengthText(passwordStrength)}</span>
              </div>
            </div>
          )}
          {errors?.password && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.password}</p>}
        </div>

        <div className="relative">
          <label style={labelStyle}>Confirm Password</label>
          <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData?.confirmPassword} onChange={handleInputChange}
            placeholder="Confirm password" disabled={isLoading}
            style={{ ...inputStyle, borderColor: errors?.confirmPassword ? ERROR : 'rgba(0,255,229,0.3)', paddingRight: '44px' }}
            onFocus={e => e.target.style.borderColor = CYAN}
            onBlur={e => e.target.style.borderColor = errors?.confirmPassword ? ERROR : 'rgba(0,255,229,0.3)'}
          />
          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} disabled={isLoading}
            style={{ position: 'absolute', right: '12px', top: '34px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
          {errors?.confirmPassword && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{errors?.confirmPassword}</p>}
        </div>

        <div className="space-y-3">
          {[
            { name: 'agreeToTerms', label: 'I ACCEPT THE TERMS OF SERVICE AND PRIVACY POLICY', error: errors?.agreeToTerms, required: true },
            { name: 'subscribeNewsletter', label: 'SUBSCRIBE TO MISSION UPDATES', required: false },
          ]?.map(item => (
            <div key={item?.name}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name={item?.name} checked={formData?.[item?.name]} onChange={handleInputChange}
                  style={{ accentColor: CYAN, marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.68rem', color: MUTED, fontFamily: MONO, letterSpacing: '0.06em' }}>{item?.label}</span>
              </label>
              {item?.error && <p style={{ color: ERROR, fontSize: '0.65rem', fontFamily: MONO, marginTop: '4px' }}>{item?.error}</p>}
            </div>
          ))}
        </div>

        <button type="submit" disabled={isLoading}
          style={{ background: 'rgba(45, 200, 150, 0.15)', border: '1px solid #2DC896', color: '#2DC896', width: '100%', padding: '12px 24px', textTransform: 'uppercase', letterSpacing: '0.1em', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontFamily: 'inherit', opacity: isLoading ? 0.7 : 1 }}>
{isLoading ? '[ INITIALIZING... ]' : '[ CREATE ACCOUNT ]'}
        </button>

        <div className="text-center pt-2">
          <p style={{ fontSize: '0.75rem', color: MUTED, fontFamily: MONO }}>
            Already signed up?{' '}
            <button onClick={() => navigate('/user-login')} type="button"
              style={{ color: CYAN, fontFamily: MONO, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.08em' }}>
              SIGN IN
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;